import { useEffect, useState } from 'react';
import { dayNumber, nextDailyAt } from '../engine/seed';

/**
 * Today's daily number, re-rendering at local midnight so "today" rolls over
 * while the app is open. Timers drift while a device sleeps, so it also
 * re-checks whenever the page becomes visible or regains focus.
 */
export function useToday(): number {
  const [today, setToday] = useState(() => dayNumber());

  useEffect(() => {
    let timer = 0;
    const sync = () => {
      setToday(dayNumber());
      window.clearTimeout(timer);
      timer = window.setTimeout(sync, Math.max(1000, nextDailyAt() - Date.now() + 250));
    };
    sync();
    const onVisible = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', sync);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', sync);
    };
  }, []);

  return today;
}

/** Milliseconds until the next daily unlocks, ticking once a second. */
export function useUntilNextDaily(): number {
  const [ms, setMs] = useState(() => nextDailyAt() - Date.now());
  useEffect(() => {
    const tick = () => setMs(Math.max(0, nextDailyAt() - Date.now()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);
  return ms;
}
